import type {
  MeasurementConfig,
  SessionValidity,
  SessionValidityInput,
  TypingSessionSummary,
} from "./types";

function round(value: number, decimals = 2): number {
  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}

function activeMinutes(activeDurationMs: number): number {
  return activeDurationMs / 60_000;
}

export function calculateRawWpm(
  printableAttempts: number,
  activeDurationMs: number,
  provisionalMetricsAfterMs = 2_000,
): number | null {
  if (
    printableAttempts < 0 ||
    activeDurationMs < provisionalMetricsAfterMs
  ) {
    return null;
  }

  const minutes = activeMinutes(activeDurationMs);
  if (minutes <= 0) {
    return null;
  }

  return round(printableAttempts / 5 / minutes);
}

export function calculateAccurateWpm(
  correctPrintableAttempts: number,
  activeDurationMs: number,
  provisionalMetricsAfterMs = 2_000,
): number | null {
  if (
    correctPrintableAttempts < 0 ||
    activeDurationMs < provisionalMetricsAfterMs
  ) {
    return null;
  }

  const minutes = activeMinutes(activeDurationMs);
  if (minutes <= 0) {
    return null;
  }

  return round(correctPrintableAttempts / 5 / minutes);
}

export function calculateNetWpm(
  rawWpm: number | null,
  uncorrectedErrors: number,
  activeDurationMs: number,
): number | null {
  if (rawWpm === null) {
    return null;
  }

  const minutes = activeMinutes(activeDurationMs);
  if (minutes <= 0) {
    return null;
  }

  return round(Math.max(0, rawWpm - uncorrectedErrors / minutes));
}

export function calculateAccuracy(
  correctAttempts: number,
  totalAttempts: number,
): number | null {
  if (totalAttempts <= 0) {
    return null;
  }

  return round(
    Math.max(0, Math.min(100, (correctAttempts / totalAttempts) * 100)),
  );
}

export function calculateFinalTextAccuracy(
  sourceText: string,
  typedText: string,
  requireFullSource = false,
): number | null {
  const denominator = requireFullSource
    ? sourceText.length
    : Math.min(Math.max(typedText.length, 0), sourceText.length);

  if (denominator <= 0) {
    return null;
  }

  let correct = 0;
  for (let index = 0; index < denominator; index += 1) {
    if (sourceText[index] === typedText[index]) {
      correct += 1;
    }
  }

  return calculateAccuracy(correct, denominator);
}

export function calculateConsistencyFromPaceSamples(
  samples: readonly number[],
  minimumSamples = 5,
): number | null {
  const usable = samples.filter(
    (sample) => Number.isFinite(sample) && sample >= 0,
  );
  if (usable.length < minimumSamples) {
    return null;
  }

  const mean =
    usable.reduce((total, sample) => total + sample, 0) / usable.length;
  if (mean <= 0) {
    return null;
  }

  const variance =
    usable.reduce((total, sample) => total + (sample - mean) ** 2, 0) /
    usable.length;
  const coefficientOfVariation = Math.sqrt(variance) / mean;
  return round(
    Math.max(0, Math.min(100, 100 * (1 - Math.min(coefficientOfVariation, 1)))),
  );
}

export function calculateRollingPaceSamples(
  correctEventTimesMs: readonly number[],
  activeDurationMs: number,
  windowMs = 5_000,
  stepMs = 1_000,
): number[] {
  if (
    activeDurationMs < windowMs ||
    windowMs <= 0 ||
    stepMs <= 0 ||
    correctEventTimesMs.length === 0
  ) {
    return [];
  }

  const samples: number[] = [];
  for (let windowEnd = windowMs; windowEnd <= activeDurationMs; windowEnd += stepMs) {
    const windowStart = windowEnd - windowMs;
    const correctCharacters = correctEventTimesMs.filter(
      (timestamp) => timestamp > windowStart && timestamp <= windowEnd,
    ).length;
    const wpm = correctCharacters / 5 / (windowMs / 60_000);
    samples.push(round(wpm));
  }

  return samples;
}

export function evaluateSessionValidity({
  sessionType,
  activeDurationMs,
  printableAttempts,
  accurateWpm,
  pasteDetected,
  timingDiscontinuityDetected,
  contentApproved,
  completed,
  config,
}: SessionValidityInput): SessionValidity {
  const minimumDurationMet =
    activeDurationMs >= config.practiceMinimumDurationMs;
  const minimumAttemptsMet =
    printableAttempts >= config.practiceMinimumAttempts;
  const benchmarkDurationMet =
    activeDurationMs >= config.benchmarkMinimumDurationMs;
  const benchmarkAttemptsMet =
    printableAttempts >= config.benchmarkMinimumAttempts;
  const plausibleSpeed =
    accurateWpm === null || accurateWpm <= config.plausibleWpmCeiling;

  const reasons: string[] = [];
  if (!minimumDurationMet) reasons.push("minimum-practice-duration");
  if (!minimumAttemptsMet) reasons.push("minimum-practice-attempts");
  if (pasteDetected) reasons.push("paste-detected");
  if (timingDiscontinuityDetected) reasons.push("timing-discontinuity");
  if (!plausibleSpeed) reasons.push("implausible-speed");
  if (!completed) reasons.push("session-incomplete");

  const isVerified =
    !pasteDetected && !timingDiscontinuityDetected && plausibleSpeed;
  const isValidPractice =
    completed && minimumDurationMet && minimumAttemptsMet && isVerified;
  const isValidBenchmark =
    isValidPractice &&
    sessionType === "benchmark" &&
    benchmarkDurationMet &&
    benchmarkAttemptsMet &&
    contentApproved;

  if (
    sessionType === "benchmark" &&
    (!benchmarkDurationMet || !benchmarkAttemptsMet)
  ) {
    reasons.push("minimum-benchmark-evidence");
  }
  if (sessionType === "benchmark" && !contentApproved) {
    reasons.push("unapproved-benchmark-content");
  }

  return {
    isValidPractice,
    isValidBenchmark,
    isVerified,
    reasons: Array.from(new Set(reasons)),
    pasteDetected,
    timingDiscontinuityDetected,
    minimumDurationMet,
    minimumAttemptsMet,
    plausibleSpeed,
  };
}

export function aggregateSessionSummaries(
  summaries: readonly TypingSessionSummary[],
): {
  activeDurationMs: number;
  printableAttempts: number;
  correctPrintableAttempts: number;
  accurateWpm: number | null;
  keystrokeAccuracy: number | null;
} {
  const activeDurationMs = summaries.reduce(
    (total, summary) => total + summary.activeDurationMs,
    0,
  );
  const printableAttempts = summaries.reduce(
    (total, summary) => total + summary.printableAttempts,
    0,
  );
  const correctPrintableAttempts = summaries.reduce(
    (total, summary) => total + summary.correctPrintableAttempts,
    0,
  );

  return {
    activeDurationMs,
    printableAttempts,
    correctPrintableAttempts,
    accurateWpm: calculateAccurateWpm(
      correctPrintableAttempts,
      activeDurationMs,
      0,
    ),
    keystrokeAccuracy: calculateAccuracy(
      correctPrintableAttempts,
      printableAttempts,
    ),
  };
}

export function withValidityOverrides(
  config: MeasurementConfig,
  minimumDurationMs?: number,
  minimumAttempts?: number,
): MeasurementConfig {
  return {
    ...config,
    practiceMinimumDurationMs:
      minimumDurationMs ?? config.practiceMinimumDurationMs,
    practiceMinimumAttempts:
      minimumAttempts ?? config.practiceMinimumAttempts,
  };
}

